/**
 * Unified events (/events) - D34, docs/UnifiedEventsDesign.md.
 *
 * Merges two data sources into one list:
 *   1. `GET /events` - official (staff-created) member events
 *   2. `GET /schedules` - the caller's personal events (schedules)
 *
 * All users see both types. Staff can create official events; all users can
 * create personal events. Type pills distinguish "Official" from "Personal".
 *
 * Registration flow is unchanged for official events. Personal events show
 * the caller's RSVP status and attendee count instead.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as eventsApi from '../api/events';
import * as schedulesApi from '../api/schedules';
import { EVENT_STATUSES } from '../api/events';
import { usePagedList } from '../lib/usePagedList';
import { useMyRegistrations } from '../lib/useMyRegistrations';
import { createdEventNotice, filterFollowingCreate } from '../lib/eventVisibility';
import { useConfirm } from '../lib/confirm';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/toast';
import Tabs from '../components/Tabs';
import Modal from '../components/Modal';
import CheckInCode from '../components/CheckInCode';
import EventForm, { emptyEventForm } from '../components/EventForm';
import PersonalEventForm, { scheduleToForm } from '../components/PersonalEventForm';
import * as usersApi from '../api/users';
import {
  ErrorAlert,
  Spinner,
  EmptyState,
  PagedListFooter,
  RelativeTime,
  SelectField,
  StatusPill,
  UserChip,
} from '../components/ui';
import { cn, ui } from '../lib/ui';

const PAGE_SIZE = 25;
const SCHEDULE_PAGE_LIMIT = 100;
const SCHEDULE_MAX_PAGES = 10;

const TABS = [
  { id: 'all', label: 'All events' },
  { id: 'mine', label: 'My events' },
  { id: 'official', label: 'Official events' },
  { id: 'personal', label: 'Personal events' },
];

/* ------------------------------------------------------ schedule fetching */

async function fetchAllSchedulePages(fetchPage, pick, onTruncated) {
  const all = [];
  let cursor = null;
  for (let page = 0; page < SCHEDULE_MAX_PAGES; page += 1) {
    const data = await fetchPage(cursor);
    pick(data).forEach(item => all.push(item));
    cursor = (data && data.lastEvaluatedKey) || null;
    if (!cursor) return all;
  }
  onTruncated();
  return all;
}

function usePersonalEvents() {
  const toast = useToast();
  const [state, setState] = useState({ loading: true, error: null, schedules: [] });
  const runIdRef = useRef(0);

  const load = useCallback(() => {
    const runId = (runIdRef.current += 1);
    setState(c => ({ ...c, loading: true, error: null }));

    const now = new Date();
    const startAfter = now.toISOString();
    const startBefore = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString();

    fetchAllSchedulePages(
      cursor =>
        schedulesApi.listSchedules({
          startAfter,
          startBefore,
          limit: SCHEDULE_PAGE_LIMIT,
          lastEvaluatedKey: cursor,
        }),
      data => (data && data.schedules) || [],
      () =>
        toast.error(
          `Only the first ${SCHEDULE_MAX_PAGES * SCHEDULE_PAGE_LIMIT} personal events were loaded.`
        )
    )
      .then(schedules => {
        if (runId !== runIdRef.current) return;
        setState({ loading: false, error: null, schedules });
      })
      .catch(err => {
        if (runId !== runIdRef.current) return;
        setState({ loading: false, error: err, schedules: [] });
      });
  }, [toast]);

  useEffect(load, [load]);
  return useMemo(() => ({ ...state, reload: load }), [state, load]);
}

/* -------------------------------------------------------- create type modal */

function CreateEventModal({ open, onClose, isStaff, onCreatePersonal, onCreateOfficial }) {
  const [mode, setMode] = useState(null); // null | 'personal' | 'official'

  useEffect(() => {
    if (!open) setMode(null);
  }, [open]);

  if (!open) return null;

  if (mode === 'official' && isStaff) {
    return (
      <Modal open onClose={onClose} title="New official event">
        <EventForm
          initial={emptyEventForm()}
          submitLabel="Create event"
          onSubmit={onCreateOfficial}
          onCancel={onClose}
        />
      </Modal>
    );
  }

  if (mode === 'personal') {
    return (
      <Modal open onClose={onClose} title="New personal event">
        <PersonalEventForm onSubmit={onCreatePersonal} onCancel={onClose} />
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title="New event">
      <p className={cn(ui.muted, 'text-body-s mb-4')}>
        What kind of event would you like to create?
      </p>
      <div className={cn(ui.grid, ui.gridMd2)}>
        <button
          type="button"
          className={cn(ui.card, 'cursor-pointer text-left')}
          onClick={() => setMode('personal')}
        >
          <h3 className={ui.h3}>Personal event</h3>
          <p className={cn(ui.muted, 'text-body-s')}>
            A calendar event for yourself. You can invite specific people.
          </p>
        </button>
        {isStaff && (
          <button
            type="button"
            className={cn(ui.card, 'cursor-pointer text-left')}
            onClick={() => setMode('official')}
          >
            <h3 className={ui.h3}>Official event</h3>
            <p className={cn(ui.muted, 'text-body-s')}>
              A member-facing event with registration, check-in, and badges.
            </p>
          </button>
        )}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------- page */

export default function Events() {
  const { isStaff: isEventStaff, user } = useAuth();
  const userId = user && user.userId;
  const confirm = useConfirm();
  const toast = useToast();
  const registrations = useMyRegistrations();
  const personalEvents = usePersonalEvents();

  const [tab, setTab] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [justRegistered, setJustRegistered] = useState(null);
  const [statusFilter, setStatusFilter] = useState('published');
  const [creating, setCreating] = useState(false);
  const [editingPersonal, setEditingPersonal] = useState(null);

  const list = usePagedList(
    cursor =>
      eventsApi
        .listEvents({
          limit: PAGE_SIZE,
          lastEvaluatedKey: cursor,
          status: isEventStaff ? statusFilter : undefined,
        })
        .then(d => ({ items: d.events || [], lastEvaluatedKey: d.lastEvaluatedKey })),
    [isEventStaff, statusFilter]
  );

  const mergedItems = useMemo(() => {
    const officialItems = list.items.map(event => ({
      ...event,
      _kind: 'official',
      _sortTime: event.startsAt,
    }));

    const personalItems = personalEvents.schedules.map(schedule => ({
      ...schedule,
      _kind: 'personal',
      _sortTime: schedule.startTime,
    }));

    const all = [...officialItems, ...personalItems];
    all.sort((a, b) => {
      const tA = a._sortTime ? new Date(a._sortTime).getTime() : 0;
      const tB = b._sortTime ? new Date(b._sortTime).getTime() : 0;
      return tA - tB;
    });
    return all;
  }, [list.items, personalEvents.schedules]);

  const visibleItems = useMemo(() => {
    if (tab === 'mine') {
      return mergedItems.filter(
        item =>
          (item._kind === 'official' && Boolean(registrations.byEventId[item.eventId])) ||
          (item._kind === 'personal' &&
            (item.requestor === userId || (item.attendees || []).includes(userId)))
      );
    }
    if (tab === 'official') {
      return mergedItems.filter(item => item._kind === 'official');
    }
    if (tab === 'personal') {
      return mergedItems.filter(item => item._kind === 'personal');
    }
    return mergedItems;
  }, [tab, mergedItems, registrations.byEventId, userId]);

  async function createOfficialEvent(payload) {
    const { event: created } = await eventsApi.createEvent(payload);
    toast.success(createdEventNotice(created.name, created.status));
    setCreating(false);
    const nextFilter = filterFollowingCreate(statusFilter, created.status);
    if (nextFilter === statusFilter) {
      list.reload();
    } else {
      setStatusFilter(nextFilter);
    }
  }

  async function createPersonalEvent(payload) {
    await schedulesApi.createSchedule(payload);
    toast.success('Personal event created.');
    setCreating(false);
    personalEvents.reload();
  }

  async function respondToPersonal(schedule, response) {
    setBusyId(schedule.id);
    try {
      await schedulesApi.respondToSchedule(schedule.id, response);
      toast.success(`Responded ${response}.`);
      personalEvents.reload();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusyId(null);
    }
  }

  async function deletePersonal(schedule) {
    const ok = await confirm({
      title: 'Cancel personal event?',
      message: `"${schedule.subject || '(no subject)'}" will be deleted and attendees will be notified.`,
      confirmLabel: 'Cancel event',
      danger: true,
    });
    if (!ok) return;
    setBusyId(schedule.id);
    try {
      await schedulesApi.deleteSchedule(schedule.id);
      toast.success('Personal event cancelled.');
      personalEvents.reload();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusyId(null);
    }
  }

  async function openEditPersonal(schedule) {
    const attendeeIds = schedule.attendees || [];
    const sw = schedule.sharedWith || { users: [], groups: [] };
    const sharedUserIds = sw.users || [];
    const resolve = async ids => {
      if (!ids.length) return [];
      const results = await Promise.allSettled(ids.map(id => usersApi.getUser(id)));
      return results.map((r, i) =>
        r.status === 'fulfilled' && r.value && r.value.user
          ? {
              userId: r.value.user.userId,
              displayName:
                [r.value.user.firstName, r.value.user.lastName].filter(Boolean).join(' ') || null,
              email: r.value.user.email,
            }
          : { userId: ids[i], displayName: null, email: null }
      );
    };
    const [resolvedAttendees, resolvedShared] = await Promise.all([
      resolve(attendeeIds),
      resolve(sharedUserIds),
    ]);
    setEditingPersonal({
      schedule,
      form: scheduleToForm(schedule, resolvedAttendees, resolvedShared),
    });
  }

  async function saveEditPersonal(payload) {
    await schedulesApi.updateSchedule(editingPersonal.schedule.id, payload);
    toast.success('Personal event updated.');
    setEditingPersonal(null);
    personalEvents.reload();
  }

  async function register(event) {
    setBusyId(event.eventId);
    try {
      const { registration } = await eventsApi.registerForEvent(event.eventId);
      registrations.setRegistration(event.eventId, registration);
      toast.success(
        `Registered for ${event.name}. Your check-in code is ${registration.checkInCode}.`
      );
      setJustRegistered({
        eventId: event.eventId,
        name: event.name,
        checkInCode: registration.checkInCode,
      });
    } catch (err) {
      toast.error(err);
    } finally {
      setBusyId(null);
    }
  }

  async function cancel(event) {
    const ok = await confirm({
      title: 'Cancel registration?',
      message: `You'll lose your spot at "${event.name}". You can register again later if space allows.`,
      confirmLabel: 'Cancel registration',
      danger: true,
    });
    if (!ok) return;
    setBusyId(event.eventId);
    try {
      await eventsApi.cancelRegistration(event.eventId);
      registrations.setRegistration(event.eventId, null);
      toast.success('Registration cancelled.');
    } catch (err) {
      toast.error(err);
    } finally {
      setBusyId(null);
    }
  }

  const loading = list.loading || registrations.loading || personalEvents.loading;
  const error = list.error || registrations.error || personalEvents.error;

  return (
    <>
      <div className={cn(ui.row, ui.spread, 'mb-4 items-start')}>
        <h1 className="text-title-l">Events</h1>
        <button type="button" className={ui.btnPrimary} onClick={() => setCreating(true)}>
          New event
        </button>
      </div>

      <div className={cn(ui.row, 'mb-4 items-end!')}>
        <Tabs
          tabs={TABS}
          value={tab}
          onChange={setTab}
          idPrefix="events-tab"
          aria-label="Filter events"
        />
        {isEventStaff && (
          <SelectField
            label="Status"
            hint="Non-staff members only ever see published events."
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            {EVENT_STATUSES.map(status => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </SelectField>
        )}
      </div>

      <div role="tabpanel" id="events-tab-panel" aria-labelledby={`events-tab-${tab}`}>
        <ErrorAlert error={error} />
        {error && !loading && (
          <button
            type="button"
            className={cn(ui.btnSecondary, 'mb-4')}
            onClick={() => {
              list.reload();
              personalEvents.reload();
            }}
          >
            Retry
          </button>
        )}

        {loading ? (
          <Spinner />
        ) : visibleItems.length === 0 ? (
          !error && (
            <EmptyState>
              {tab === 'mine'
                ? "You haven't registered for any upcoming events yet."
                : tab === 'official'
                  ? 'No upcoming official events right now - check back soon.'
                  : tab === 'personal'
                    ? 'No upcoming personal events on your calendar.'
                    : 'No events scheduled right now - check back soon.'}
            </EmptyState>
          )
        ) : (
          <div className={cn(ui.grid, ui.gridMd2)}>
            {visibleItems.map(item => {
              if (item._kind === 'official') {
                const event = item;
                const registration = registrations.byEventId[event.eventId];
                const isRegistered = Boolean(registration);
                const busy = busyId === event.eventId;
                return (
                  <section key={`official-${event.eventId}`} className={ui.card}>
                    <h2 className="mb-2 text-title-s">
                      <Link to={`/events/${encodeURIComponent(event.eventId)}`}>{event.name}</Link>
                    </h2>
                    <p className={cn(ui.row, 'text-body-s')}>
                      <span className={ui.pillInfo}>Official</span>
                      {event.status && event.status !== 'published' && (
                        <StatusPill status={event.status} />
                      )}
                      {event.accreditedOnly && <span className={ui.pillInfo}>Accredited only</span>}
                    </p>
                    <p className={cn(ui.muted, 'text-body-s')}>
                      <RelativeTime iso={event.startsAt} />
                      {event.endsAt && (
                        <>
                          {' '}
                          - <RelativeTime iso={event.endsAt} />
                        </>
                      )}
                      {event.venue ? ` - ${event.venue}` : ''}
                    </p>
                    <p className={cn(ui.muted, 'text-body-s')}>
                      Capacity: {event.capacity == null ? 'Unlimited' : event.capacity}
                    </p>
                    {event.description && <p>{event.description}</p>}

                    {isRegistered && (
                      <div className={ui.alertInfo}>
                        <p>
                          <strong>Registered</strong>
                        </p>
                        <CheckInCode code={registration.checkInCode} />
                      </div>
                    )}

                    <div className={ui.row}>
                      {isRegistered ? (
                        <button
                          type="button"
                          className={ui.btnSecondary}
                          disabled={busy}
                          onClick={() => cancel(event)}
                        >
                          {busy ? 'Working…' : 'Cancel registration'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className={ui.btnPrimary}
                          disabled={busy}
                          onClick={() => register(event)}
                        >
                          {busy ? 'Working…' : 'Register'}
                        </button>
                      )}
                      <Link
                        className={ui.btnSecondary}
                        to={`/events/${encodeURIComponent(event.eventId)}`}
                      >
                        View details
                      </Link>
                    </div>
                  </section>
                );
              }

              // Personal event (schedule)
              const schedule = item;
              const isRequestor = schedule.requestor === userId;
              const isAttendee = (schedule.attendees || []).includes(userId);
              const attendeeCount = (schedule.attendees || []).length;
              const pBusy = busyId === schedule.id;
              const calDate = schedule.startTime ? schedule.startTime.slice(0, 7) : '';
              return (
                <section key={`personal-${schedule.id}`} className={ui.card}>
                  <h2 className="mb-2 text-title-s">{schedule.subject || '(no subject)'}</h2>
                  <p className={cn(ui.row, 'text-body-s')}>
                    <span className={ui.pillNeutral}>Personal</span>
                    {isRequestor && <span className={ui.pillNeutral}>organizer</span>}
                    {!isRequestor && schedule.response && <StatusPill status={schedule.response} />}
                    {schedule.recurrence && <span className={ui.pillNeutral}>repeats</span>}
                    {schedule.showTimeAs && schedule.showTimeAs !== 'busy' && (
                      <span className={ui.pillNeutral}>{schedule.showTimeAs}</span>
                    )}
                    {schedule.visibility === 'shared' && (
                      <span className={ui.pillInfo}>shared</span>
                    )}
                  </p>
                  <p className={cn(ui.muted, 'text-body-s')}>
                    <RelativeTime iso={schedule.startTime} />
                    {schedule.endTime && (
                      <>
                        {' '}
                        - <RelativeTime iso={schedule.endTime} />
                      </>
                    )}
                    {schedule.location ? ` - ${schedule.location}` : ''}
                  </p>
                  {attendeeCount > 0 && (
                    <p className="text-body-s">
                      <span className={ui.muted}>
                        {attendeeCount} attendee{attendeeCount === 1 ? '' : 's'}:{' '}
                      </span>
                      {(schedule.attendees || []).map(uid => (
                        <UserChip key={uid} userId={uid} />
                      ))}
                    </p>
                  )}
                  {((schedule.sharedWith &&
                    schedule.sharedWith.users &&
                    schedule.sharedWith.users.length > 0) ||
                    (schedule.sharedWith &&
                      schedule.sharedWith.groups &&
                      schedule.sharedWith.groups.length > 0)) && (
                    <p className={cn(ui.muted, 'text-body-s')}>
                      Shared with{' '}
                      {schedule.sharedWith.users.length > 0 &&
                        `${schedule.sharedWith.users.length} viewer${schedule.sharedWith.users.length === 1 ? '' : 's'}`}
                      {schedule.sharedWith.users.length > 0 &&
                        schedule.sharedWith.groups.length > 0 &&
                        ', '}
                      {schedule.sharedWith.groups.length > 0 &&
                        `${schedule.sharedWith.groups.length} group${schedule.sharedWith.groups.length === 1 ? '' : 's'}`}
                    </p>
                  )}
                  {schedule.message && <p>{schedule.message}</p>}
                  {isAttendee && !isRequestor && (
                    <div className={cn(ui.row, 'mb-2')}>
                      {['accepted', 'tentative', 'declined'].map(r => (
                        <button
                          key={r}
                          type="button"
                          className={schedule.response === r ? ui.btnPrimary : ui.btnSecondary}
                          disabled={pBusy || schedule.response === r}
                          onClick={() => respondToPersonal(schedule, r)}
                          aria-label={`Mark ${schedule.subject || 'event'} as ${r}`}
                        >
                          {pBusy ? 'Working…' : r}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className={ui.row}>
                    <Link
                      className={ui.btnSecondary}
                      to={
                        calDate
                          ? `/schedules?view=month&date=${calDate}&select=${encodeURIComponent(schedule.id)}`
                          : `/schedules?select=${encodeURIComponent(schedule.id)}`
                      }
                    >
                      View on calendar
                    </Link>
                    {isRequestor && (
                      <>
                        <button
                          type="button"
                          className={ui.btnSecondary}
                          disabled={pBusy}
                          onClick={() => openEditPersonal(schedule)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className={ui.btnDanger}
                          disabled={pBusy}
                          onClick={() => deletePersonal(schedule)}
                        >
                          {pBusy ? 'Working…' : 'Cancel event'}
                        </button>
                      </>
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {tab !== 'personal' && (
          <PagedListFooter
            hasMore={list.hasMore}
            loadingMore={list.loadingMore}
            onLoadMore={list.loadMore}
            endLabel={list.items.length ? 'No more official events.' : null}
          />
        )}
      </div>

      <Modal
        open={Boolean(justRegistered)}
        onClose={() => setJustRegistered(null)}
        title="You're registered"
        footer={
          <button type="button" className={ui.btnPrimary} onClick={() => setJustRegistered(null)}>
            Done
          </button>
        }
      >
        {justRegistered && (
          <>
            <p>
              You&rsquo;re registered for <strong>{justRegistered.name}</strong>. Bring this code to
              check in at the door:
            </p>
            <CheckInCode code={justRegistered.checkInCode} />
          </>
        )}
      </Modal>

      <CreateEventModal
        open={creating}
        onClose={() => setCreating(false)}
        isStaff={isEventStaff}
        onCreatePersonal={createPersonalEvent}
        onCreateOfficial={createOfficialEvent}
      />

      <Modal
        open={Boolean(editingPersonal)}
        onClose={() => setEditingPersonal(null)}
        title="Edit personal event"
      >
        {editingPersonal && (
          <PersonalEventForm
            initial={editingPersonal.form}
            submitLabel="Save changes"
            warnOnTimeChange
            onSubmit={saveEditPersonal}
            onCancel={() => setEditingPersonal(null)}
          />
        )}
      </Modal>
    </>
  );
}
